import React, { useMemo, useState } from 'react';
import { USAMap } from '@mirawision/usa-map-react';
import { Tooltip } from '@mui/material';
import propTypes from 'prop-types';
import {
  getCarrierStateOptions,
  getCarrierStateUnavailabilityReasons,
  getPendingStates,
  STATE_UNAVAILABILITY_REASON_LABELS
} from 'helpers/appointments/appointments';
import { arrayDifference } from 'helpers/arrays';
import { useSelector } from 'react-redux';
import {
  getStateCodeFromStateName,
  getStateNameFromStateCode
} from 'helpers/states';
import { VALID_STATE_CODES } from 'helpers/carriers/carriers';
import { getCompany } from 'helpers/getCompanyVariables';
import { cn } from 'lib/utils';
import useMobile from 'hooks/useMobile';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import { DISCLAIMER_PLACEMENTS } from 'helpers/factory/carrierFactory';

const company = getCompany();

const isSunshine = company === 'sunshine';

/** Sunshine map label: Active; O’Neill (and other non-Sunshine): Confirmed */
const CONFIRMED_OR_ACTIVE_LABEL = isSunshine ? 'Active' : 'Confirmed';

const OFFERED_NOT_AVAILABLE_LABEL = 'Offered (Not available)';

const PENDING_AGENT_ACTION_COLOR = 'var(--color-semantic-danger)';

const STATE_GROUP_COLORS = {
  [CONFIRMED_OR_ACTIVE_LABEL]: 'var(--color-secondary)',
  Pending: 'var(--color-pending)',
  'Pending Agent Action': PENDING_AGENT_ACTION_COLOR,
  'Appointment Available': 'var(--color-primary-light)',
  [OFFERED_NOT_AVAILABLE_LABEL]: '#94a3b8',
  'Not Offered': '#dbdbdb'
};

const LEGEND_ORDER = [
  CONFIRMED_OR_ACTIVE_LABEL,
  'Pending',
  'Pending Agent Action',
  'Appointment Available',
  OFFERED_NOT_AVAILABLE_LABEL,
  'Not Offered'
];

const LEGEND_DESCRIPTIONS = {
  [OFFERED_NOT_AVAILABLE_LABEL]:
    'The carrier offers appointments in this state, but you cannot request them right now. Hover the state on the map to see why.',
  'Appointment Available':
    'Appointment Available: you are licensed in this state, the carrier offers appointments there, and you are not yet appointed—so you may request an appointment.'
};

function getStatesWithType(states, type) {
  if (!Array.isArray(states)) return [];

  return states.map(state => ({
    code: getStateCodeFromStateName(state),
    name: state,
    type
  }));
}

/** Legend / state hover fills — avoid mixing very light CSS vars with black (reads as dark gray). */
const STATE_GROUP_HIGHLIGHT_COLORS = {
  'Appointment Available': 'var(--color-primary-emphasis)',
  [OFFERED_NOT_AVAILABLE_LABEL]: '#64748b',
  [CONFIRMED_OR_ACTIVE_LABEL]: 'var(--color-primary-darkened)',
  Pending: 'var(--color-pending)',
  'Pending Agent Action': 'var(--color-semantic-danger)',
  'Not Offered': '#b8b8b8'
};

function getHighlightFill(type, baseFill) {
  return STATE_GROUP_HIGHLIGHT_COLORS[type] ?? darkenFill(baseFill);
}

/** Darken fill for hover when no dedicated highlight color exists. */
function darkenFill(fill) {
  return `color-mix(in srgb, ${fill} 68%, black)`;
}

function buildCodeToTypeMap(statesList) {
  const map = {};
  statesList.forEach(s => {
    map[s.code] = s.type;
  });
  return map;
}

// { [stateCode]: string[] } with the reasons an offered state can't be requested
function buildCodeToReasonsMap(carrierData, user, stateNames) {
  const map = {};
  stateNames.forEach(stateName => {
    const code = getStateCodeFromStateName(stateName);
    if (!code) return;
    map[code] = getCarrierStateUnavailabilityReasons(
      carrierData,
      user,
      stateName
    ).map(reason => STATE_UNAVAILABILITY_REASON_LABELS[reason]);
  });
  return map;
}

// map hover content: state name, its group, and why it is not available when that applies
function renderStateTooltip(code, type, reasons = []) {
  return (
    <div style={{ maxWidth: '220px' }}>
      <strong>{getStateNameFromStateCode(code)}</strong>
      <br />
      <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>{type}</span>
      {reasons.length > 0 && (
        <ul
          style={{
            margin: '4px 0 0',
            paddingLeft: '16px',
            fontSize: '0.8125rem',
            opacity: 0.9
          }}
        >
          {reasons.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SelectedCarrierWithStates({ activeCarrier }) {
  const [hoveredStateCode, setHoveredStateCode] = useState(null);
  const [hoverLegendGroup, setHoverLegendGroup] = useState(null);

  const user = useSelector(state => state.user.zohoData);
  const { actionableAppointments } = useSelector(state => state.appointments);
  const { confirmed, pending, statesOffered, carrier: name } = activeCarrier;

  const isMobile = useMobile();

  const tempOptions = arrayDifference(statesOffered, [
    ...(Array.isArray(confirmed) ? confirmed : []),
    ...(Array.isArray(pending) ? pending : [])
  ]);

  const options = getCarrierStateOptions(
    activeCarrier.carrierData,
    user,
    tempOptions
  );

  const offeredNotAvailable = arrayDifference(tempOptions, options);
  const offeredNotAvailableStates = getStatesWithType(
    offeredNotAvailable,
    OFFERED_NOT_AVAILABLE_LABEL
  );

  const unavailabilityReasonsByCode = useMemo(
    () =>
      buildCodeToReasonsMap(
        activeCarrier.carrierData,
        user,
        offeredNotAvailable
      ),
    [activeCarrier.carrierData, user, offeredNotAvailable.join(',')]
  );

  const apptAvaialbleStates = getStatesWithType(
    options,
    'Appointment Available'
  );

  const confirmedStates = getStatesWithType(
    confirmed,
    CONFIRMED_OR_ACTIVE_LABEL
  );

  const tempActionablePendingStates = getPendingStates(
    actionableAppointments.filter(appt => appt.carrier === name),
    confirmed
  );

  const pendingStates = getStatesWithType(
    (Array.isArray(pending) ? pending : []).filter(
      state => !tempActionablePendingStates.includes(state)
    ),
    'Pending'
  );

  const actionablePendingStates = getStatesWithType(
    tempActionablePendingStates,
    'Pending Agent Action'
  );

  const states = [
    ...offeredNotAvailableStates,
    ...apptAvaialbleStates,
    ...confirmedStates,
    ...pendingStates,
    ...actionablePendingStates
  ];

  const customStates = useMemo(() => {
    const codeToType = buildCodeToTypeMap(states);
    const result = {};

    VALID_STATE_CODES.forEach(code => {
      const type = codeToType[code] ?? 'Not Offered';
      const baseFill = STATE_GROUP_COLORS[type];

      let fill = baseFill;
      if (hoverLegendGroup) {
        if (type === hoverLegendGroup) {
          fill = getHighlightFill(type, baseFill);
        }
      } else if (hoveredStateCode === code) {
        fill = getHighlightFill(type, baseFill);
      }

      result[code] = {
        fill,
        stroke: '#fff',
        label:
          type === 'Appointment Available'
            ? {
                enabled: true,
                render: abbrev => (
                  <tspan style={{ fontWeight: 900, fontSize: '1rem' }}>
                    {abbrev}
                  </tspan>
                )
              }
            : { enabled: true },
        tooltip: {
          enabled: !isMobile,
          render: () =>
            renderStateTooltip(code, type, unavailabilityReasonsByCode[code])
        },
        onHover: isMobile ? undefined : () => setHoveredStateCode(code),
        onLeave: isMobile ? undefined : () => setHoveredStateCode(null)
      };
    });

    return result;
  }, [
    states,
    hoveredStateCode,
    hoverLegendGroup,
    isMobile,
    unavailabilityReasonsByCode
  ]);

  return (
    <div className='show-scrollbar flex min-h-0 w-full flex-1 flex-col gap-2 rounded-xl bg-white'>
      {activeCarrier.carrierData.disclaimer?.placements?.has(
        DISCLAIMER_PLACEMENTS.AVAILABLE_STATES_MAP
      ) && (
        <div className='w-full min-w-0 shrink-0'>
          <CarrierDisclaimer
            disclaimer={activeCarrier.carrierData.disclaimer}
          />
        </div>
      )}
      <div className='flex min-h-0 w-full flex-1 flex-col'>
        <div className='flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3 lg:min-w-0'>
          <div className='min-h-0 flex-1 overflow-x-hidden lg:overflow-x-visible'>
            <div className='flex w-full justify-center'>
              <div className='mx-auto box-border w-full min-w-0 max-w-5xl'>
                <div
                  className={cn(
                    'flex h-[250px] w-full items-center justify-center lg:h-[450px]',
                    '[&_svg]:box-border [&_svg]:h-full [&_svg]:w-full [&_svg]:max-w-full'
                  )}
                >
                  <USAMap
                    mapSettings={{
                      width: '100%',
                      height: '100%'
                    }}
                    customStates={customStates}
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className={cn(
              'flex w-full shrink-0 flex-row flex-wrap items-start justify-start gap-x-6 gap-y-2.5',
              'border-t border-grey-lighter bg-white pb-1 pt-3 text-sm',
              'sm:justify-center sm:gap-x-8'
            )}
          >
            {LEGEND_ORDER.map(group => {
              const tooltipTitle = LEGEND_DESCRIPTIONS[group];
              const legendButton = (
                <button
                  type='button'
                  className='flex cursor-pointer items-center border-none bg-transparent p-0 text-left text-inherit'
                  onMouseEnter={() => setHoverLegendGroup(group)}
                  onMouseLeave={() => setHoverLegendGroup(null)}
                >
                  <span
                    className='mr-2 inline-block h-3 w-3 shrink-0 rounded-full'
                    style={{ backgroundColor: STATE_GROUP_COLORS[group] }}
                  />
                  <span
                    className={cn(
                      group === 'Appointment Available' && 'font-semibold'
                    )}
                  >
                    {group}
                  </span>
                </button>
              );

              return tooltipTitle ? (
                <Tooltip key={group} title={tooltipTitle} placement='top'>
                  {legendButton}
                </Tooltip>
              ) : (
                <React.Fragment key={group}>{legendButton}</React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

SelectedCarrierWithStates.propTypes = {
  activeCarrier: propTypes.object.isRequired
};
